import React, { Component } from "react";
import Player from "@vimeo/player";
// Components
import ChoiceComponent from "../../components/ChoiceComponent/ChoiceComponent";
import DevControls from "../../components/DevControls/DevControls";
import { Navigation } from "../../components/Navigation/Navigation";
import { Notification } from "../../components/Notification/Notification";
import { IntroComponent } from "../../components/IntroComponent/IntroComponent";
import { EndComponent } from "../../components/EndComponent/EndComponent";

// Data
import videos from "../../json/data";
// Interfaces
import { video, notification } from "../../interfaces";

interface state {
	firstTime: boolean;
	showEndPlate: boolean;
	videos: video[];
	paused: boolean;
	currentVideo: video;
	loadingVideo: boolean;
	videoIndex: number;
	activeNotification: notification | null;
	lastNotification: notification | null;
	menuOpen: boolean;
}

export default class StoryPage extends Component<{}, state> {
	saveStateToLocal = (name: string, event: object | number | boolean) => {
		localStorage.setItem(`${name}`, JSON.stringify(event));
	};
	loadStateFromLocal = (
		name: string,
		initial: number | string | boolean | object | []
	) => {
		return localStorage.getItem(name)
			? JSON.parse(localStorage.getItem(name) || "{}")
			: initial;
	};
	player: Player | any;
	state: state = {
		firstTime: this.loadStateFromLocal("firstTime", true),
		showEndPlate: false,
		videos: videos,
		menuOpen: true,
		paused: false,
		loadingVideo: false,
		videoIndex: this.loadStateFromLocal("videoIndex", 0),
		currentVideo: this.loadStateFromLocal("currentVideo", videos[0]),
		activeNotification: null,
		lastNotification: null,
	};
	componentDidMount() {
		this.state.firstTime
			? console.log("First time!")
			: console.log("Not the first time!");
		// Let's create the video object!
		this.player = new Player("player");
		// Let's load the video!
		this.getVimeoData();
		this.initVideo();
		// Keybinds
		document.addEventListener("keydown", this.playPauseSpaceKey, false);
		this.scrollChapterIntoView();
	}
	componentWillUnmount() {
		document.removeEventListener("keydown", this.playPauseSpaceKey, false);
	}
	componentDidUpdate() {
		if (this.state.loadingVideo) {
			document.title = this.state.currentVideo.name + " – Tynd tråd";
		}
	}
	introComponent = () => {};
	getVimeoData = async () => {
		for (let i = 0; i < videos.length; i++) {
			const response = await fetch(
				`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videos[i].vimeoId}/`
			);
			const myJson = await response.json();
			const arr = [];
			const thumbnailUrl = myJson.thumbnail_url.replace(
				"_295x166.jpg",
				"_480x270.jpg"
			);
			const duration = myJson.duration;
			arr.push(thumbnailUrl, duration);
			var videosCopy = [...this.state.videos];
			videosCopy[i].thumbnail = thumbnailUrl;
			videosCopy[i].duration = duration;
			this.setState({ videos: videosCopy });
		}
		this.saveStateToLocal("currentVideo", this.state.currentVideo);
	};
	playPauseSpaceKey = (event: KeyboardEvent) => {
		if (event.key === " ") {
			this.toggleMenu();
		}
	};
	toggleMenu = () => {
		this.setState(
			(prevState) => ({
				menuOpen: !prevState.menuOpen,
			}),
			() => {
				setTimeout(() => {
					if (this.state.menuOpen === true) {
						this.scrollChapterIntoView();
					}
				}, 150);
			}
		);
		this.playPause(!this.state.paused);
	};
	hideMenu = () => {
		this.setState({ menuOpen: false });
	};
	showMenu = () => {
		this.setState({ menuOpen: true }, () => {
			setTimeout(() => {
				if (this.state.menuOpen === true) {
					this.scrollChapterIntoView();
				}
			}, 150);
		});
	};
	scrollChapterIntoView = () => {
		const current = document.querySelector("li.current");
		if (current) {
			current.scrollIntoView({
				behavior: "smooth",
				inline: "center",
			});
		}
	};
	initVideo() {
		// Force volume and play
		this.player.setVolume(1);
		this.state.firstTime || this.state.menuOpen
			? this.playPause(true)
			: this.playPause(false);
		this.saveStateToLocal("currentVideo", this.state.currentVideo);
		this.player.on(
			"timeupdate",
			(data: { seconds: number; duration: number }) => this.timeUpdate(data)
		);
		// Log video seconds
		this.player.on(
			"timeupdate",
			(data: { seconds: number; duration: number }) => console.log(data.seconds)
		);
		this.player.on("ended", () => {
			if (
				this.state.currentVideo?.choice?.defaultChoice &&
				!this.state.currentVideo?.lastVideo
			) {
				this.goToVideo(this.state.currentVideo.choice.defaultChoice);
			} else {
				this.showEndPlate();
			}
		});
	}
	showEndPlate = () => {
		this.setState({ showEndPlate: true });
	};
	timeUpdate = (data: { seconds: number }) => {
		// timeupdate variables
		let notifications = this.state.currentVideo.notifications;
		let playbackDuration = data.seconds;
		let currentVideoDuration = this.state.currentVideo.duration;
		// Choices
		let timeToAnswer = this.state.currentVideo.choice.duration;
		if (playbackDuration > currentVideoDuration - timeToAnswer) {
			if (!this.state.currentVideo.choice.active) {
				this.showChoice();
				this.setState({
					menuOpen: false,
				});
			}
		} else {
			if (this.state.currentVideo.choice.active) {
				this.showChoice(false);
			}
		}
		// Notifications
		const nots = notifications.find(
			(not: notification) =>
				not.timecode >= data.seconds - 0.25 && not.timecode <= data.seconds
		);
		if (nots && this.state.activeNotification?.timecode !== nots.timecode) {
			this.showNotification(nots);
		}
	};

	showNotification = (notification: notification) => {
		this.setState({ activeNotification: notification });
		// console.log(notification.desc);
		setTimeout(() => {
			this.hideNotification(notification);
		}, notification.duration * 1000);
	};
	hideNotification = (notification: notification) => {
		this.setState({
			activeNotification: null,
			lastNotification: notification,
		});
	};
	showIntro = () => {
		this.setState({
			firstTime: true,
		});
	};
	showChoice = (show = true) => {
		const currentVideo = this.state.currentVideo;
		currentVideo.choice.active = show;
		this.setState({ currentVideo });
	};

	goToVideo = (event: string): Promise<void> => {
		return new Promise((resolve) => {
			// Check if the event is valid before proceeding
			if (!event || typeof event !== "string") {
				console.error("Invalid video ID provided");
				resolve();
				return;
			}

			// Find the video to change to
			const nextVideo = videos.find((video) => video.id === event);
			if (!nextVideo) {
				console.error(`Video with ID ${event} not found`);
				resolve();
				return;
			}

			// Start fade out
			this.setState({ loadingVideo: true }, () => {
				console.log("loadingVideo set to true"); // Debug log
				resolve();

				// Wait for fade out to complete
				setTimeout(() => {
					// Update state with new video information
					this.setState(
						{
							showEndPlate: false,
							currentVideo: {
								...nextVideo,
								choice: { ...nextVideo.choice, active: false },
							},
							videoIndex: videos.findIndex((video) => video.id === event),
							activeNotification: null,
						},
						() => {
							// Turn off the timeupdate event for changing video
							this.player.off("timeupdate");

							// Load and play the new video
							this.player
								.loadVideo(nextVideo.vimeoId)
								.then((id: number) => {
									this.initVideo();
									console.log("Loaded video: " + id);

									// Set up the play event listener for fade in
									this.player.on("play", () => {
										// Start fade in
										this.setState({ loadingVideo: false });
									});

									// Save the current video index to local storage
									this.saveStateToLocal("videoIndex", this.state.videoIndex);
								})
								.catch((error: Error) => {
									console.error("Error loading video:", error);
									// Ensure loadingVideo is set to false even if there's an error
									this.setState({ loadingVideo: false });
								});
						}
					);
				}, 300); // Adjust this duration to match your fade out animation
			});
		});
	};

	playPause = (shouldPause: boolean) => {
		if (this.player) {
			this.player[shouldPause ? "pause" : "play"]().then(() => {
				this.setState({
					paused: shouldPause,
				});
				console.log(`Paused:`, shouldPause);
			});
		}
	};

	seekVideo = (goForwards: boolean) => {
		const active = this.state.activeNotification;
		this.player.getCurrentTime().then((seconds: number) => {
			const ten = goForwards ? seconds + 10 : seconds - 10;
			const five = goForwards ? seconds + 5 : seconds - 5;
			this.player
				.setCurrentTime(ten)
				.then(() => {
					console.log(goForwards ? "10 seconds »" : "« 10 seconds");
					if (active && active.timecode + active.duration <= ten) {
						console.log("Hiding");
						this.hideNotification(active);
					}
				})
				.catch((error: { name: string }) => {
					switch (error.name) {
						case "RangeError":
							this.player
								.setCurrentTime(five)
								.then(() => {
									console.log(goForwards ? "5 seconds »" : "« 5 seconds");
									this.timeUpdate({ seconds: five });
									if (active && active.timecode + active.duration <= five) {
										console.log("Hiding");
										this.hideNotification(active);
									}
								})
								.catch(() => {});
							break;
					}
				});
		});
	};
	setFirstTime = (boolean: boolean) => {
		this.setState(
			{
				firstTime: boolean,
			},
			() => {
				this.saveStateToLocal("firstTime", this.state.firstTime);
			}
		);
	};
	render() {
		return (
			<div className={"wrapper " + (this.state.firstTime ? "intro" : "")}>
				<EndComponent
					goToVideo={this.goToVideo}
					showEndPlate={this.state.showEndPlate}
				/>
				<IntroComponent
					setFirstTime={this.setFirstTime}
					toggleMenu={this.toggleMenu}
					firstTime={this.state.firstTime}
				/>
				<main className={"main" + (this.state.loadingVideo ? " loading" : "")}>
					<Notification
						hideNotification={this.hideNotification}
						activeNotification={this.state.activeNotification}
						lastNotification={this.state.lastNotification}
					/>
					<ChoiceComponent
						goToVideo={this.goToVideo}
						active={this.state.currentVideo.choice.active}
						choices={this.state.currentVideo.choice.choices}
						duration={this.state.currentVideo.choice.duration}
						paused={this.state.paused}
					/>
					<Navigation
						loadingVideo={this.state.loadingVideo}
						key={this.state.videoIndex}
						showIntro={this.showIntro}
						firstTime={this.state.firstTime}
						videoIndex={this.state.videoIndex}
						localStorage={{
							setItem: this.saveStateToLocal,
							getItem: this.loadStateFromLocal,
						}}
						menu={{
							open: this.state.menuOpen,
							toggle: this.toggleMenu,
							hide: this.hideMenu,
							show: this.showMenu,
						}}
						choiceShown={this.state.currentVideo.choice.active}
						videos={this.state.videos}
						goToVideo={this.goToVideo}
						paused={this.state.paused}
					/>
					<DevControls
						menuOpen={this.state.menuOpen}
						hideMenu={this.hideMenu}
						activeNotification={this.state.activeNotification}
						firstTime={this.state.firstTime}
						paused={this.state.paused}
						playPause={this.playPause}
						seekVideo={this.seekVideo}
					/>
					<div
						className={
							"video-component " +
							(this.state.loadingVideo ? "loading" : "") +
							(this.state.menuOpen ? "menu-toggled" : "")
						}
					>
						<div
							data-vimeo-id={this.state.currentVideo.vimeoId}
							data-vimeo-autoplay="true"
							data-vimeo-background="true"
							data-vimeo-playsinline="true"
							data-vimeo-muted="false"
							data-vimeo-loop="false"
							data-vimeo-responsive="true"
							id="player"
							className="vimeo-wrapper "
						></div>
					</div>
				</main>
			</div>
		);
	}
}
